import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import LogoMark from "./LogoMark";
import { useLang } from "@/lib/i18n";

const scrollTo = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

function LangToggle({ onPick }) {
  const { lang, setLang } = useLang();
  const pick = (l) => {
    setLang(l);
    onPick?.();
  };
  return (
    <div className="flex items-center gap-1.5 rounded-sm border border-[#2D2520] p-1" data-testid="lang-toggle">
      {["no", "en"].map((l) => (
        <button
          key={l}
          onClick={() => pick(l)}
          data-testid={`lang-toggle-${l}`}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors duration-300 ${
            lang === l
              ? "bg-[#D48C46] text-[#110E0C]"
              : "text-[#A69C95] hover:text-[#F9F6F0]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#110E0C]/70 border-b border-[#2D2520]"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={() => go("#hjem")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <LogoMark size={34} />
          <span className="font-serif text-xl tracking-tight text-[#F9F6F0] group-hover:text-[#D48C46] transition-colors duration-300">
            Bordverket
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {t.nav.links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={l.testid}
              className="text-sm font-medium text-[#A69C95] hover:text-[#F9F6F0] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <LangToggle />
          </div>
          <button
            onClick={() => go("#kontakt")}
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center rounded-sm bg-[#D48C46] px-5 py-2.5 text-sm font-semibold text-[#110E0C] hover:bg-[#F0A862] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            className="md:hidden text-[#F9F6F0] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label={t.nav.menuLabel}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-[#2D2520] bg-[#110E0C]/95"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {t.nav.links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`${l.testid}-mobile`}
                  className="text-left text-base text-[#A69C95] hover:text-[#F9F6F0] transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <LangToggle onPick={() => setOpen(false)} />
              <button
                onClick={() => go("#kontakt")}
                data-testid="nav-cta-button-mobile"
                className="mt-2 inline-flex items-center justify-center rounded-sm bg-[#D48C46] px-5 py-3 text-sm font-semibold text-[#110E0C] hover:bg-[#F0A862] transition-colors duration-300"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
